import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import InputBase from '@mui/material/InputBase';
import AppButton from '../../components/common/AppButton';
import { FieldLabel, CONTROL_SX } from '../../components/common/FormField';

/** The server's floor; checked here too so a short one never leaves the browser. */
const MIN_LENGTH = 6;

/**
 * An Admin setting someone else's password.
 *
 * Deliberately not a "generate and email it" flow — there's no mail path in this
 * app — so the Admin types a password and passes it on themselves. It's shown in
 * clear because they have to be able to read it out.
 */
export default function ResetPasswordDialog({ open, user, saving, onCancel, onConfirm }) {
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setPassword('');
      setTouched(false);
    }
  }, [open]);

  const tooShort = password.length > 0 && password.length < MIN_LENGTH;
  const ready = password.length >= MIN_LENGTH;

  const submit = () => {
    setTouched(true);
    if (ready) onConfirm(password);
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: { sx: { borderRadius: '16px', border: '1px solid var(--border)', background: 'var(--card)', overflow: 'hidden' } },
      }}
    >
      <Box sx={{ height: 4, background: 'linear-gradient(90deg,#0d9488,#14b8a6,var(--prism-accent))' }} />

      <Box sx={{ p: '20px 22px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ fontFamily: 'var(--disp)', fontSize: 17, fontWeight: 700 }}>Reset password</Box>
        <AppButton mini onClick={onCancel} sx={{ minWidth: 32, width: 32, height: 32, p: 0 }}>✕</AppButton>
      </Box>

      <Box sx={{ p: '8px 22px 20px' }}>
        <Box sx={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink)' }}>
          Set a new password for <b>{user?.name}</b>.
        </Box>
        <Box sx={{ fontSize: 12, color: 'var(--muted)', mt: 0.75, mb: 2 }}>
          They'll need it to sign in — pass it on and ask them to change it.
        </Box>

        <FieldLabel>New password</FieldLabel>
        <InputBase
          fullWidth
          autoFocus
          sx={{ ...CONTROL_SX, fontFamily: 'var(--mono)' }}
          placeholder={`At least ${MIN_LENGTH} characters`}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onBlur={() => setTouched(true)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
        />
        {(tooShort || (touched && !password)) && (
          <Box sx={{ fontSize: 12, color: 'var(--crit)', mt: 0.75 }}>
            {password ? `Must be at least ${MIN_LENGTH} characters` : 'Enter a password'}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex', gap: 1.25, justifyContent: 'flex-end',
          p: '14px 22px', borderTop: '1px solid var(--border)', background: 'var(--card-2)',
        }}
      >
        <AppButton onClick={onCancel} disabled={saving}>Cancel</AppButton>
        <AppButton tone="primary" onClick={submit} disabled={saving || !ready}>
          {saving ? 'Setting…' : 'Set password'}
        </AppButton>
      </Box>
    </Dialog>
  );
}
